import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Box,
  Button,
  Typography
} from '../../ui';
import { Download } from 'lucide-react';

const CustomerSelector = ({
  customers,
  selectedIds,
  onSelectionChange
}) => {
  const isSelected = (id) => selectedIds.includes(id);

  const handleToggle = (id) => {
    const currentIndex = selectedIds.indexOf(id);
    const newSelectedIds = [...selectedIds];

    if (currentIndex === -1) {
      newSelectedIds.push(id);
    } else {
      newSelectedIds.splice(currentIndex, 1);
    }

    onSelectionChange(newSelectedIds);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      const allIds = customers.map((c) => c.id);
      onSelectionChange(allIds);
      return;
    }
    onSelectionChange([]);
  };

  const handleExportCSV = () => {
    if (selectedIds.length === 0) return;

    const selectedCustomers = customers.filter(c => selectedIds.includes(c.id));

    // Headers: name, mobile, address
    // Data mapping: custName, mob, address
    const headers = ['name', 'mobile'];
    const csvRows = [
      headers.join(','),
      ...selectedCustomers.map(c => [
        `"${c.custName || ''}"`,
        `"91${c.mob || ''}"`
      ].join(','))
    ];

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `customers_export_${new Date().getTime()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Button
          variant="contained"
          size="large"
          startIcon={<Download size={18} />}
          onClick={handleExportCSV}
          disabled={selectedIds.length === 0}
          sx={{ fontWeight: 800, borderRadius: 2 }}
        >
          Export CSV
        </Button>
      </Box>
      <TableContainer sx={{ flex: 1, maxHeight: '100%', border: 'none' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Checkbox
                  size="small"
                  indeterminate={selectedIds.length > 0 && selectedIds.length < customers.length}
                  checked={customers.length > 0 && selectedIds.length === customers.length}
                  onChange={handleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', typography: 'overline', fontSize: '1.5rem', fontWeight: 700, color: 'text.secondary', letterSpacing: '0.1em' }}>Name</TableCell>
              <TableCell sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', typography: 'overline', fontSize: '1.5rem', fontWeight: 700, color: 'text.secondary', letterSpacing: '0.1em' }}>Mobile</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer) => (
              <TableRow
                key={customer.id}
                hover
                onClick={() => handleToggle(customer.id)}
                role="checkbox"
                aria-checked={isSelected(customer.id)}
                selected={isSelected(customer.id)}
                sx={{
                  cursor: 'pointer',
                  '&.Ui-selected': {
                    bgcolor: 'rgba(1, 51, 62, 0.08) !important', // Using primary color #01333e with alpha
                    '&:hover': {
                      bgcolor: 'rgba(1, 51, 62, 0.12) !important',
                    }
                  }
                }}
              >
                <TableCell padding="checkbox">
                  <Checkbox size="small" checked={isSelected(customer.id)} />
                </TableCell>
                <TableCell sx={{ fontWeight: 500, fontFamily: 'Vazir', fontSize: '1.25rem' }}>{customer.custName}</TableCell>
                <TableCell sx={{ fontValues: 'monospaced', fontFamily: 'Vazir', fontSize: '1.25rem', color: 'black' }}>{customer.mob}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default CustomerSelector;
